import { useMemo } from "react";
import { isAdministeredDose, type ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import {
  buildLabResults,
  isAbnormalLabStatus,
  type LabReportRecord,
} from "@/features/patient/diagnostics/utils/adaptDiagnostics";
import { examAndVitalsVisits, type ExamVitalsVisit } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const dateOnly = (v: unknown): string =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) if (typeof v === "string" && v.trim() !== "") return v;
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
];

function fmt(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export type TimelineType =
  | "lab_result"
  | "prescription"
  | "visit_summary"
  | "imaging"
  | "immunization"
  | "procedure"
  | "condition"
  | "exam_vitals";

export interface TimelineItemData {
  id: string;
  monthKey: string; // "2026-03" — for grouping/sorting
  month: string; // "MARCH 2026"
  title: string;
  rawDate: string; // "2026-03-18" ("" when unknown)
  date: string; // "Mar 18, 2026" ("" when unknown)
  createdAt: string; // formatted record.createdAt
  meta: string; // secondary label (lab / facility / "" when unknown)
  type: TimelineType;
  status: string; // raw FHIR status ("" when unknown)
  performerDisplay: string; // "" when unknown
  displayName: string; // "" when unknown
  record: ParsedRecord;
  /** lab_result: the report / panel with ONLY its own results (same grouping as Diagnostics). */
  lab?: LabReportRecord;
  /** exam_vitals: one visit day's vital signs and exam findings. */
  examVisit?: ExamVitalsVisit;
}

function isImagingReport(f: Record<string, unknown>): boolean {
  const category = str(f.category).toLowerCase();
  const contentType = str(f.attachment_content_type).toLowerCase();
  const name = str(f.display_name).toLowerCase();
  return (
    category.includes("imag") ||
    category.includes("radiol") ||
    contentType.includes("image") ||
    /x-?ray|mri|ct scan|ultrasound|sonogram|mammogram|radiograph/.test(name)
  );
}

function adaptTimelineItem(record: ParsedRecord): TimelineItemData | null {
  const f = record.fields;
  let type: TimelineType;
  let rawDate = "";
  let title = "";
  let meta = "";
  let performerDisplay = "";

  switch (record.resourceType) {
    case "Encounter":
      type = "visit_summary";
      rawDate = firstDate(f.start_date, f.end_date);
      title = firstStr(f.encounter_type, f.encounter_class_display, f.reason_display) || "Visit";
      meta = firstStr(f.location_display, f.service_provider);
      performerDisplay = firstStr(f.practitioner_name, f.practitioner_display);
      break;
    case "MedicationRequest":
      type = "prescription";
      rawDate = firstDate(f.authored_on);
      title = record.displayName || firstStr(f.medication_name, f.medication_codeable_text) || "Medication";
      meta = isAdministeredDose(record)
        ? ["Administered", firstStr(f.dosage_text)].filter(Boolean).join(" · ")
        : firstStr(f.dosage_text);
      break;
    case "Immunization":
      type = "immunization";
      rawDate = firstDate(f.occurrence_date);
      title = firstStr(f.vaccine_display) || "Immunization";
      meta = firstStr(f.site, f.route);
      break;
    case "Procedure":
      type = "procedure";
      rawDate = firstDate(f.performed_date, f.performed_start, f.performed_end);
      title = firstStr(f.display_name) || "Procedure";
      meta = firstStr(f.reason_display);
      break;
    case "Condition":
      type = "condition";
      rawDate = firstDate(f.onset_date, f.recorded_date, f.asserted_date);
      title = firstStr(f.display_name) || "Condition";
      meta = firstStr(f.category);
      break;
    case "DiagnosticReport":
      // A lab report is one entry with its own results (labTimelineItems);
      // only an imaging report is listed from here.
      if (!isImagingReport(f)) return null;
      type = "imaging";
      rawDate = firstDate(f.issued_date, f.effective_date);
      title = firstStr(f.display_name, f.code) || "Imaging study";
      meta = firstStr(f.performer_display);
      performerDisplay = firstStr(f.performer_display, f.interpreter_display);
      break;
    // A single result is never its own entry: labs are inside their report /
    // panel, vitals and exam findings inside that day's "Exam & vitals".
    default:
      return null;
  }

  if (!rawDate) return null;
  const d = new Date(`${rawDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;

  return {
    id: record.id,
    monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
    month: `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`,
    title,
    rawDate,
    date: fmt(rawDate),
    createdAt: fmt(dateOnly(record.createdAt)) || "—",
    meta,
    type,
    status: firstStr(f.status, f.clinical_status),
    performerDisplay,
    displayName: firstStr(f.display_name),
    record,
  };
}

export interface TimelineResult {
  items: TimelineItemData[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

function datedItem(rawDate: string): Pick<TimelineItemData, "monthKey" | "month" | "rawDate" | "date"> | null {
  if (!rawDate) return null;
  const d = new Date(`${rawDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return {
    monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
    month: `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`,
    rawDate,
    date: fmt(rawDate),
  };
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** One entry per lab report / panel, holding only its own results. */
export function labTimelineItems(records: ParsedRecord[]): TimelineItemData[] {
  const byId = new Map(records.map((r) => [r.id, r]));
  const out: TimelineItemData[] = [];
  for (const lab of buildLabResults(records)) {
    const when = datedItem(lab.collectionDate);
    const record = byId.get(lab.diagnosticReportId) ?? byId.get(lab.results[0]?.id ?? "");
    if (!when || !record) continue;
    const abnormal = lab.results.filter((r) => isAbnormalLabStatus(r.status)).length;
    out.push({
      id: `lab:${lab.id}`,
      ...when,
      title: lab.testName,
      createdAt: fmt(dateOnly(record.createdAt)) || "—",
      meta: [plural(lab.results.length, "result"), abnormal ? `${abnormal} abnormal` : ""].filter(Boolean).join(" · "),
      type: "lab_result",
      status: lab.status,
      performerDisplay: lab.orderingProvider,
      displayName: lab.testName,
      record,
      lab,
    });
  }
  return out;
}

/** One "Exam & vitals" entry per visit day. */
export function examTimelineItems(records: ParsedRecord[]): TimelineItemData[] {
  const byId = new Map(records.map((r) => [r.id, r]));
  const out: TimelineItemData[] = [];
  for (const visit of examAndVitalsVisits(records)) {
    const when = datedItem(visit.date);
    const first = [...visit.vitals, ...visit.exam, ...visit.other][0];
    const record = first ? byId.get(first.id) : undefined;
    if (!when || !record) continue;
    out.push({
      id: `exam:${visit.date}`,
      ...when,
      title: "Exam & vitals",
      createdAt: fmt(dateOnly(record.createdAt)) || "—",
      meta: [
        visit.vitals.length ? plural(visit.vitals.length, "vital sign") : "",
        visit.exam.length ? plural(visit.exam.length, "exam finding") : "",
        visit.other.length ? plural(visit.other.length, "other observation") : "",
      ].filter(Boolean).join(" · "),
      type: "exam_vitals",
      status: "",
      performerDisplay: "",
      displayName: "Exam & vitals",
      record,
      examVisit: visit,
    });
  }
  return out;
}

export function useTimeline(): TimelineResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const items = useMemo(() => {
    return [
      ...records.map(adaptTimelineItem).filter((i): i is TimelineItemData => i !== null),
      ...labTimelineItems(records),
      ...examTimelineItems(records),
    ].sort((a, b) => b.rawDate.localeCompare(a.rawDate));
  }, [records]);
  return { items, isLoading, error, refresh };
}

/** History of one observation (matched by display name) across every date it
 *  appears on — powers the trend chart. Returns [] when there are fewer than
 *  two data points so the caller can show an empty-chart state. */
export function useObservationHistoryByName(name: string): { label: string; value: number }[] {
  const { records } = useParsedRecords();
  return useMemo(() => {
    if (!name) return [];
    const points = records
      .filter(
        (r) =>
          r.resourceType === "Observation" &&
          str(r.fields.display_name).toLowerCase() === name.toLowerCase(),
      )
      .map((r) => {
        const d = firstDate(r.fields.effective_date, r.fields.issued);
        const value = parseFloat(str(r.fields.value_quantity));
        return { d, value };
      })
      .filter((p) => p.d && !Number.isNaN(p.value))
      .sort((a, b) => a.d.localeCompare(b.d));
    if (points.length < 2) return [];
    return points.map((p) => {
      const dt = new Date(`${p.d}T00:00:00`);
      return {
        label: `${MONTHS_SHORT[dt.getMonth()]} '${String(dt.getFullYear()).slice(2)}`,
        value: p.value,
      };
    });
  }, [records, name]);
}
